"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaBriefcase,
  FaCalendarAlt,
  FaCheckCircle,
  FaClipboardCheck,
  FaMapMarkerAlt,
} from "react-icons/fa";
import Header from "../../components/IntranetHeader";
import Footer from "../../components/Footer";
import { supabase } from "../../supabaseClient";
import { formatProfileName } from "../../utils/profileName";
import ApplicationChat from "../../components/ApplicationChat";

function normaliseQuestions(value) {
  if (!Array.isArray(value)) return [];

  return value.map((question, index) => {
    if (typeof question === "string") {
      return {
        id: `legacy-${index}`,
        label: question,
        type: "long",
        required: true,
        help_text: "",
        placeholder: "",
        options: [],
      };
    }

    return {
      id: question?.id || `question-${index}`,
      label: question?.label || question?.question || `Question ${index + 1}`,
      type: question?.type || "long",
      required: question?.required !== false,
      help_text: question?.help_text || "",
      placeholder: question?.placeholder || "",
      options: Array.isArray(question?.options) ? question.options : [],
    };
  });
}

function formatDate(value, includeTime = false) {
  if (!value) return "Not specified";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not specified";

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    ...(includeTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
}

function ApplicationStatus({ status }) {
  const key = String(status || "Received").toLowerCase().replace(/\s+/g, "-");
  return <span className={`pguh-apply-status pguh-apply-status--${key}`}>{status || "Received"}</span>;
}

export default function Vacancy() {
  const { id } = useParams();
  const [vacancy, setVacancy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [application, setApplication] = useState(null);
  const [identity, setIdentity] = useState({
    roblox_username: "",
    discord_username: "",
    email: "",
    roleplay_name: "",
  });
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    if (id) loadPage();
  }, [id]);

  async function loadPage() {
    setLoading(true);
    setError("");

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      setError("You must be signed in to view this vacancy.");
      setLoading(false);
      return;
    }

    const [vacancyResult, profileResult, applicationResult] = await Promise.all([
      supabase.from("dc_vacancies").select("*").eq("id", id).maybeSingle(),
      supabase.from("profiles").select("*").eq("id", user.id).maybeSingle(),
      supabase
        .from("dc_applications")
        .select("*")
        .eq("vacancy_id", id)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);

    if (vacancyResult.error) {
      console.error("Unable to load vacancy:", vacancyResult.error);
      setError(vacancyResult.error.message || "The vacancy could not be loaded.");
    }

    setVacancy(vacancyResult.data || null);
    setApplication(applicationResult.data || null);

    const profile = profileResult.data;
    const existing = applicationResult.data;

    setIdentity({
      roblox_username: existing?.roblox_username || profile?.roblox_name || "",
      discord_username: existing?.discord_username || profile?.discord_name || "",
      email: existing?.email || profile?.email || user.email || "",
      roleplay_name: existing?.roleplay_name || formatProfileName(profile, ""),
    });

    if (existing?.submitted_answers && typeof existing.submitted_answers === "object") {
      setAnswers(existing.submitted_answers);
    }

    setLoading(false);
  }

  const customQuestions = useMemo(() => normaliseQuestions(vacancy?.questions), [vacancy?.questions]);
  const closingPassed = useMemo(() => {
    if (!vacancy?.closing_date) return false;
    const closing = new Date(vacancy.closing_date).getTime();
    return !Number.isNaN(closing) && closing <= Date.now();
  }, [vacancy?.closing_date]);

  function updateIdentity(field, value) {
    setIdentity((current) => ({ ...current, [field]: value }));
  }

  function updateAnswer(questionId, value) {
    setAnswers((current) => ({ ...current, [questionId]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!vacancy || application || submitting) return;

    setError("");

    const requiredIdentity = [
      ["Roblox username", identity.roblox_username],
      ["Discord username", identity.discord_username],
      ["Email", identity.email],
      ["Roleplay Name", identity.roleplay_name],
    ];

    const missingIdentity = requiredIdentity.find(([, value]) => !String(value || "").trim());
    if (missingIdentity) {
      setError(`${missingIdentity[0]} is required.`);
      return;
    }

    const missingQuestion = customQuestions.find(
      (question) => question.required && !String(answers[question.id] || "").trim(),
    );
    if (missingQuestion) {
      setError(`Please answer “${missingQuestion.label}”.`);
      return;
    }

    if (closingPassed || vacancy.status !== "Open" || !vacancy.is_active) {
      setError("This vacancy is no longer accepting applications.");
      return;
    }

    setSubmitting(true);

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      setError("Your session has expired. Sign in again before submitting your application.");
      setSubmitting(false);
      return;
    }

    const cleanedAnswers = {};
    customQuestions.forEach((question) => {
      const value = answers[question.id];
      if (value !== undefined && value !== null) {
        cleanedAnswers[question.id] = typeof value === "string" ? value.trim() : value;
      }
    });

    const { data, error: submitError } = await supabase
      .from("dc_applications")
      .insert({
        vacancy_id: vacancy.id,
        user_id: user.id,
        roblox_username: identity.roblox_username.trim(),
        discord_username: identity.discord_username.trim(),
        email: identity.email.trim(),
        roleplay_name: identity.roleplay_name.trim(),
        submitted_answers: cleanedAnswers,
        status: "Received",
      })
      .select("*")
      .single();

    if (submitError) {
      console.error("Unable to submit application:", submitError);
      setError(submitError.message || "Your application could not be submitted.");
    } else {
      setApplication(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    setSubmitting(false);
  }

  function renderQuestion(question, index) {
    const common = {
      id: `question-${question.id}`,
      value: answers[question.id] || "",
      onChange: (event) => updateAnswer(question.id, event.target.value),
      required: question.required,
    };

    return (
      <div className="pguh-apply-question" key={question.id}>
        <div className="pguh-apply-question__number">{index + 5}</div>
        <div className="pguh-apply-question__body">
          <label htmlFor={`question-${question.id}`}>
            {question.label}
            {question.required && <span className="pguh-apply-required"> Required</span>}
          </label>
          {question.help_text && <p className="pguh-apply-hint">{question.help_text}</p>}

          {question.type === "long" && (
            <textarea {...common} rows="6" placeholder={question.placeholder || ""} />
          )}

          {question.type === "short" && (
            <input {...common} type="text" placeholder={question.placeholder || ""} />
          )}

          {question.type === "select" && (
            <select {...common}>
              <option value="">Select an answer</option>
              {(question.options || []).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          )}

          {question.type === "yes_no" && (
            <div className="pguh-apply-radio-group">
              {["Yes", "No"].map((option) => (
                <label key={option}>
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    value={option}
                    checked={answers[question.id] === option}
                    onChange={(event) => updateAnswer(question.id, event.target.value)}
                    required={question.required}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="pguh-apply-loading">Loading vacancy…</main>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <main className="flex-1 pguh-apply-page">
        <div className="nhsuk-width-container">
          <Link className="pguh-apply-back" to="/intranet/vacancies">
            <FaArrowLeft aria-hidden="true" /> Back to vacancies
          </Link>

          {!vacancy ? (
            <div className="pguh-apply-empty">
              <h1>Vacancy unavailable</h1>
              <p>This vacancy could not be found or is not currently available to staff.</p>
            </div>
          ) : (
            <>
              <section className="pguh-apply-hero">
                <div>
                  <span className="pguh-eyebrow">Internal vacancy</span>
                  <h1>{vacancy.title}</h1>
                  <p>{vacancy.description || "Further details are provided below."}</p>
                </div>
                <span className={`pguh-apply-open-badge ${closingPassed ? "is-closed" : ""}`}>
                  {closingPassed || vacancy.status !== "Open" || !vacancy.is_active ? "Closed" : "Open"}
                </span>
              </section>

              <div className="pguh-apply-layout">
                <div className="pguh-apply-main">
                  <section className="pguh-apply-card pguh-apply-details">
                    <h2>About this vacancy</h2>
                    <div className="pguh-apply-meta">
                      <div>
                        <FaBriefcase aria-hidden="true" />
                        <span><strong>Department</strong>{vacancy.department || "Not specified"}</span>
                      </div>
                      <div>
                        <FaMapMarkerAlt aria-hidden="true" />
                        <span><strong>Location</strong>{vacancy.location || "Not specified"}</span>
                      </div>
                      <div>
                        <FaCalendarAlt aria-hidden="true" />
                        <span><strong>Closing date</strong>{vacancy.closing_date ? formatDate(vacancy.closing_date, true) : "No closing date"}</span>
                      </div>
                    </div>

                    {vacancy.requirements && (
                      <div className="pguh-apply-requirements">
                        <h3>Requirements and eligibility</h3>
                        <p>{vacancy.requirements}</p>
                      </div>
                    )}
                  </section>

                  {application ? (
                    <>
                    <section className="pguh-apply-card pguh-apply-receipt">
                      <FaCheckCircle aria-hidden="true" />
                      <div>
                        <span className="pguh-eyebrow">Application received</span>
                        <h2>Your application has been submitted</h2>
                        <p>
                          Submitted on {formatDate(application.created_at, true)}. Management can now review your application.
                        </p>
                        <div className="pguh-apply-receipt__status">
                          <strong>Current status</strong>
                          <ApplicationStatus status={application.status} />
                        </div>
                      </div>
                    </section>
                    <ApplicationChat applicationId={application.id} />
                    </>
                  ) : (
                    <section className="pguh-apply-card">
                      <div className="pguh-apply-form-heading">
                        <span className="pguh-eyebrow">Application form</span>
                        <h2>Apply for this vacancy</h2>
                        <p>
                          {vacancy.application_intro ||
                            "Complete all required questions. Your answers are sent directly to the management recruitment team."}
                        </p>
                      </div>

                      {error && (
                        <div className="nhsuk-error-summary" role="alert">
                          <h3 className="nhsuk-error-summary__title">There is a problem</h3>
                          <div className="nhsuk-error-summary__body">{error}</div>
                        </div>
                      )}

                      <form onSubmit={handleSubmit}>
                        <div className="pguh-apply-section-heading">
                          <span>Section 1</span>
                          <div>
                            <h3>Applicant details</h3>
                            <p>These four questions are required for every vacancy.</p>
                          </div>
                        </div>

                        <div className="pguh-apply-identity-grid">
                          <label>
                            <span><strong>1. Roblox username</strong><em>Required</em></span>
                            <input
                              value={identity.roblox_username}
                              onChange={(event) => updateIdentity("roblox_username", event.target.value)}
                              required
                              autoComplete="off"
                            />
                          </label>
                          <label>
                            <span><strong>2. Discord username</strong><em>Required</em></span>
                            <input
                              value={identity.discord_username}
                              onChange={(event) => updateIdentity("discord_username", event.target.value)}
                              required
                              autoComplete="off"
                            />
                          </label>
                          <label>
                            <span><strong>3. Email</strong><em>Required</em></span>
                            <input
                              type="email"
                              value={identity.email}
                              onChange={(event) => updateIdentity("email", event.target.value)}
                              required
                              autoComplete="email"
                            />
                          </label>
                          <label>
                            <span><strong>4. Roleplay Name</strong><em>Required</em></span>
                            <input
                              value={identity.roleplay_name}
                              onChange={(event) => updateIdentity("roleplay_name", event.target.value)}
                              required
                              autoComplete="name"
                            />
                          </label>
                        </div>

                        {customQuestions.length > 0 && (
                          <>
                            <div className="pguh-apply-section-heading pguh-apply-section-heading--custom">
                              <span>Section 2</span>
                              <div>
                                <h3>Vacancy questions</h3>
                                <p>Answer the questions set by the recruitment team.</p>
                              </div>
                            </div>
                            <div className="pguh-apply-custom-questions">
                              {customQuestions.map(renderQuestion)}
                            </div>
                          </>
                        )}

                        <div className="pguh-apply-confirmation">
                          <FaClipboardCheck aria-hidden="true" />
                          <p>
                            By submitting, you confirm the information in this application is accurate to the best of your knowledge.
                          </p>
                        </div>

                        <button
                          className="pguh-apply-submit"
                          type="submit"
                          disabled={submitting || closingPassed || vacancy.status !== "Open" || !vacancy.is_active}
                        >
                          {submitting ? "Submitting application…" : "Submit application"}
                        </button>
                      </form>
                    </section>
                  )}
                </div>

                <aside className="pguh-apply-sidebar">
                  <section className="pguh-apply-card">
                    <span className="pguh-eyebrow">Application summary</span>
                    <h2>Before you apply</h2>
                    <dl>
                      <div><dt>Fixed questions</dt><dd>4</dd></div>
                      <div><dt>Vacancy questions</dt><dd>{customQuestions.length}</dd></div>
                      <div><dt>Total questions</dt><dd>{4 + customQuestions.length}</dd></div>
                      <div><dt>Status</dt><dd>{application ? application.status : vacancy.status}</dd></div>
                    </dl>
                    <p>
                      Your application is linked to your signed-in staff account and is visible to authorised management staff.
                    </p>
                  </section>
                </aside>
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />

      <style>{`
        .pguh-apply-page { padding: 28px 0 64px; background: var(--nhs-light-grey); }
        .pguh-apply-loading { display: grid; min-height: 60vh; place-items: center; font-weight: 700; }
        .pguh-apply-back { display: inline-flex; align-items: center; gap: 8px; margin-bottom: 16px; color: var(--nhs-blue); font-weight: 700; }
        .pguh-apply-hero { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; margin-bottom: 20px; padding: 30px 34px; border-left: 8px solid var(--nhs-light-blue); color: #fff; background: linear-gradient(115deg, var(--nhs-dark-blue), var(--nhs-blue)); box-shadow: var(--pguh-shadow); }
        .pguh-apply-hero h1, .pguh-apply-hero p, .pguh-apply-hero .pguh-eyebrow { color: #fff; }
        .pguh-apply-hero h1 { max-width: 900px; margin: 0 0 8px; }
        .pguh-apply-hero p { max-width: 820px; margin: 0; }
        .pguh-apply-open-badge { flex: 0 0 auto; padding: 7px 12px; color: #00401e; background: #cce5d6; font-weight: 700; text-transform: uppercase; }
        .pguh-apply-open-badge.is-closed { color: #6f120b; background: #f7d4d1; }
        .pguh-apply-layout { display: grid; grid-template-columns: minmax(0, 1fr) 300px; align-items: start; gap: 20px; }
        .pguh-apply-main { display: grid; gap: 20px; }
        .pguh-apply-sidebar { position: sticky; top: 18px; }
        .pguh-apply-card { padding: 26px; border: 1px solid var(--pguh-border); background: #fff; box-shadow: var(--pguh-shadow); }
        .pguh-apply-card h2 { margin-top: 0; }
        .pguh-apply-meta { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
        .pguh-apply-meta > div { display: flex; align-items: flex-start; gap: 10px; padding: 12px 13px; background: var(--nhs-light-grey); }
        .pguh-apply-meta svg { flex: 0 0 auto; margin-top: 3px; color: var(--nhs-blue); }
        .pguh-apply-meta span { display: flex; flex-direction: column; }
        .pguh-apply-meta strong { color: var(--nhs-dark-grey); font-size: .76rem; text-transform: uppercase; }
        .pguh-apply-requirements { margin-top: 20px; padding: 16px; border-left: 5px solid var(--nhs-light-blue); background: #f7fafc; }
        .pguh-apply-requirements h3 { margin: 0 0 8px; }
        .pguh-apply-requirements p { margin: 0; white-space: pre-wrap; }
        .pguh-apply-form-heading { margin-bottom: 22px; padding-bottom: 18px; border-bottom: 1px solid var(--pguh-border); }
        .pguh-apply-form-heading h2 { margin: 0 0 7px; }
        .pguh-apply-form-heading p { margin: 0; color: var(--nhs-dark-grey); }
        .pguh-apply-section-heading { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 14px; }
        .pguh-apply-section-heading > span { flex: 0 0 auto; padding: 5px 8px; color: #fff; background: var(--nhs-dark-blue); font-size: .76rem; font-weight: 700; text-transform: uppercase; }
        .pguh-apply-section-heading h3 { margin: 0; }
        .pguh-apply-section-heading p { margin: 2px 0 0; color: var(--nhs-dark-grey); }
        .pguh-apply-section-heading--custom { margin-top: 28px; padding-top: 22px; border-top: 1px solid var(--pguh-border); }
        .pguh-apply-identity-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
        .pguh-apply-identity-grid label { display: flex; flex-direction: column; gap: 6px; }
        .pguh-apply-identity-grid label > span { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
        .pguh-apply-identity-grid em, .pguh-apply-required { color: var(--nhs-red); font-size: .75rem; font-style: normal; font-weight: 700; text-transform: uppercase; }
        .pguh-apply-identity-grid input, .pguh-apply-question input, .pguh-apply-question textarea, .pguh-apply-question select { width: 100%; min-height: 44px; border: 2px solid var(--nhs-dark-grey); padding: 8px 10px; background: #fff; }
        .pguh-apply-question textarea { resize: vertical; }
        .pguh-apply-custom-questions { display: grid; gap: 14px; }
        .pguh-apply-question { display: grid; grid-template-columns: 38px minmax(0, 1fr); gap: 12px; padding: 16px; border: 1px solid var(--pguh-border); border-left: 5px solid var(--nhs-light-blue); background: #fbfcfd; }
        .pguh-apply-question__number { display: grid; width: 34px; height: 34px; place-items: center; color: #fff; background: var(--nhs-blue); font-weight: 700; }
        .pguh-apply-question__body > label:first-child { display: block; margin-bottom: 6px; font-weight: 700; }
        .pguh-apply-hint { margin: -1px 0 8px; color: var(--nhs-dark-grey); font-size: .88rem; }
        .pguh-apply-radio-group { display: flex; flex-wrap: wrap; gap: 10px; }
        .pguh-apply-radio-group label { display: flex; min-width: 120px; align-items: center; gap: 9px; padding: 10px 12px; border: 2px solid var(--nhs-dark-grey); background: #fff; font-weight: 700; }
        .pguh-apply-radio-group input { width: 20px; min-height: auto; height: 20px; }
        .pguh-apply-confirmation { display: flex; align-items: flex-start; gap: 12px; margin-top: 24px; padding: 14px; border-left: 5px solid var(--nhs-light-blue); background: var(--nhs-light-grey); }
        .pguh-apply-confirmation svg { flex: 0 0 auto; margin-top: 3px; color: var(--nhs-dark-blue); }
        .pguh-apply-confirmation p { margin: 0; }
        .pguh-apply-submit { min-height: 48px; margin-top: 18px; padding: 9px 24px; border: 0; border-bottom: 4px solid #00401e; color: #fff; background: var(--nhs-green); font-weight: 700; cursor: pointer; }
        .pguh-apply-submit:hover:not(:disabled) { background: var(--nhs-dark-green); }
        .pguh-apply-submit:disabled { opacity: .55; }
        .pguh-apply-sidebar dl { display: grid; gap: 8px; margin: 0 0 16px; }
        .pguh-apply-sidebar dl > div { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 8px 10px; background: var(--nhs-light-grey); }
        .pguh-apply-sidebar dt { color: var(--nhs-dark-grey); font-weight: 700; }
        .pguh-apply-sidebar dd { margin: 0; font-weight: 700; }
        .pguh-apply-sidebar p { margin: 0; color: var(--nhs-dark-grey); font-size: .9rem; }
        .pguh-apply-receipt { display: flex; align-items: flex-start; gap: 18px; border-left: 7px solid var(--nhs-green); }
        .pguh-apply-receipt > svg { flex: 0 0 auto; margin-top: 4px; color: var(--nhs-green); font-size: 2rem; }
        .pguh-apply-receipt h2 { margin: 0 0 7px; }
        .pguh-apply-receipt p { margin: 0 0 15px; color: var(--nhs-dark-grey); }
        .pguh-apply-receipt__status { display: flex; align-items: center; gap: 12px; }
        .pguh-apply-status { display: inline-flex; padding: 4px 8px; color: #003087; background: #d8e7f3; font-size: .75rem; font-weight: 700; text-transform: uppercase; }
        .pguh-apply-status--under-review { color: #594300; background: #fff1b8; }
        .pguh-apply-status--accepted { color: #00401e; background: #cce5d6; }
        .pguh-apply-status--denied { color: #6f120b; background: #f7d4d1; }
        .pguh-apply-empty { min-height: 300px; padding: 36px; border: 1px solid var(--pguh-border); background: #fff; box-shadow: var(--pguh-shadow); }
        @media (max-width: 920px) {
          .pguh-apply-layout { grid-template-columns: 1fr; }
          .pguh-apply-sidebar { position: static; }
          .pguh-apply-meta { grid-template-columns: 1fr; }
        }
        @media (max-width: 680px) {
          .pguh-apply-hero { align-items: flex-start; flex-direction: column; padding: 24px 20px; }
          .pguh-apply-identity-grid { grid-template-columns: 1fr; }
          .pguh-apply-card { padding: 20px; }
        }
      `}</style>
    </div>
  );
}
